import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Camera, Upload, Check, X, Pencil, Trash2, Package, Snowflake, Sun, ChevronDown, ChevronRight, ChevronUp, SprayCan, Bath, ImagePlus, Eye, Wrench, Receipt, PenLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import SafeReceiptCard from "@/components/SafeReceiptCard";

import { useAuth } from "@/lib/AuthContext";

// Defensive string coercion — production data can have non-string fields
// (e.g. dates serialized as objects). Strings/numbers pass through; anything
// else is stringified to prevent React's "Objects are not valid as a child"
// crash that whitescreens the page.
const asText = (v, fallback = "") => {
  if (v == null) return fallback;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return fallback; }
};
const asNumber = (v, fallback = 0) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = parseFloat(typeof v === "string" ? v.replace(",", ".") : v);
  return Number.isFinite(n) ? n : fallback;
};

const STORAGE_TYPES = [
  { value: "fridge", label: "Kjølevare", icon: Package, color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "freezer", label: "Frysevare", icon: Snowflake, color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { value: "dry", label: "Tørrproviant", icon: Sun, color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "cleaning", label: "Renhold", icon: SprayCan, color: "bg-sailcloth-100 text-navy-800 border-navy-100" },
  { value: "bathroom", label: "Toalett & Bad", icon: Bath, color: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "non_food", label: "Ikke matvare (skipp)", icon: Wrench, color: "bg-stone-100 text-stone-600 border-stone-300" },
];

export default function ReceiptScanPage() {
  const { user } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [appendingScan, setAppendingScan] = useState(false);
  const [rescanningId, setRescanningId] = useState(null);
  const [activeTrip, setActiveTrip] = useState(null);
  const [trips, setTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [expandedReceiptIds, setExpandedReceiptIds] = useState(() => new Set());
  const [currentTripOnly, setCurrentTripOnly] = useState(true); // filter kvitteringar til nåværande tur
  const [activeBoat, setActiveBoat] = useState(null);
  const [activeShift, setActiveShift] = useState("1");
  const fileInputRef = useRef(null);
  const appendInputRef = useRef(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ store_name: "", total_amount: "", date: new Date().toISOString().slice(0, 10), notes: "" });
  const [savingManual, setSavingManual] = useState(false);

  const submitManualReceipt = async () => {
    const total = parseFloat(String(manualForm.total_amount).replace(",", "."));
    if (!total || total <= 0) { toast.error("Fyll inn ei sum større enn 0"); return; }
    setSavingManual(true);
    try {
      await api.addManualReceipt({
        store_name: manualForm.store_name.trim(),
        total_amount: total,
        date: manualForm.date || new Date().toISOString().slice(0, 10),
        notes: manualForm.notes.trim(),
        trip_id: selectedTripId || activeTrip?.id || "",
      });
      toast.success(`${total.toLocaleString("nb-NO")} kr lagt til budsjettet`);
      setManualOpen(false);
      setManualForm({ store_name: "", total_amount: "", date: new Date().toISOString().slice(0, 10), notes: "" });
      loadReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikkje lagra");
    } finally { setSavingManual(false); }
  };

  const toggleExpanded = (id) => {
    setExpandedReceiptIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const loadActiveTrip = useCallback(async () => {
    try {
      const res = await api.getActiveTrip();
      setActiveTrip(res.data);
      if (res.data?.id) setSelectedTripId(res.data.id);
    } catch (err) { console.warn("loadActiveTrip failed", err); /* no active trip */ }
  }, []);

  const loadTrips = useCallback(async () => {
    try {
      const res = await api.getTrips();
      setTrips(res.data || []);
    } catch (err) { console.warn("loadTrips failed", err); }
  }, []);

  const loadReceipts = useCallback(async () => {
    try {
      const res = await api.getReceipts();
      setReceipts(res.data);
    } catch (err) { console.warn("loadReceipts failed", err); }
  }, []);

  useEffect(() => { loadActiveTrip(); loadTrips(); loadReceipts(); }, [loadActiveTrip, loadTrips, loadReceipts]);

  // Load active boat (used to compute current trip dato-range)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const boatRes = await api.getActiveBoat().catch(() => ({ data: null }));
        if (!cancelled) {
          setActiveBoat(boatRes.data);
          setActiveShift(user?.preferred_shift || "1");
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user?.preferred_shift]);

  // Compute the [start, end) date range for the active shift's currently
  // active trip. Used to filter receipts. Returns null if shift-config is missing.
  const currentTripRange = (() => {
    if (!activeBoat) return null;
    const cfg = activeShift === "1" ? (activeBoat.shift1 || {}) : (activeBoat.shift2 || {});
    if (!cfg.start_date) return null;
    const cycleDays = cfg.cycle_days || 21;
    const firstTrip = cfg.first_trip_number || 1;
    const base = new Date(cfg.start_date); base.setHours(0,0,0,0);
    const today = new Date(); today.setHours(0,0,0,0);
    const diffDays = Math.floor((today - base) / 86400000);
    if (diffDays < 0) return null;
    const currentTrip = firstTrip + Math.floor(diffDays / (cycleDays * 2)) * 2;
    const tripOffset = Math.floor((currentTrip - firstTrip) / 2);
    const start = new Date(base);
    start.setDate(start.getDate() + tripOffset * cycleDays * 2);
    const end = new Date(start);
    end.setDate(end.getDate() + cycleDays);
    return { start, end, tripNumber: currentTrip };
  })();

  // Filter receipts by date range when "Kun denne turen" is active.
  const filteredReceipts = (() => {
    if (!currentTripOnly || !currentTripRange) return receipts;
    return (receipts || []).filter((r) => {
      const d = (r.date || "").trim();
      if (!d) return false;
      try {
        const rd = new Date(d + "T00:00:00Z"); rd.setHours(0,0,0,0);
        return rd >= currentTripRange.start && rd < currentTripRange.end;
      } catch { return false; }
    });
  })();

  const handleScan = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter((f) => f.type.startsWith("image/"));
    if (valid.length === 0) { toast.error("Kun bildefiler støttes"); return; }
    setScanning(true);
    try {
      const formData = new FormData();
      const isMulti = valid.length > 1;
      if (isMulti) {
        valid.forEach((f) => formData.append("files", f));
      } else {
        formData.append("file", valid[0]);
      }
      formData.append("trip_id", selectedTripId || activeTrip?.id || "");
      formData.append("store_name", "");
      const res = isMulti ? await api.scanReceiptMulti(formData) : await api.scanReceipt(formData);
      const data = res.data || {};
      const itemsWithIds = (data.items || []).map((it, idx) => ({
        ...it,
        _key: it.id || `rcp-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
      }));
      setReceipt({ ...data, items: itemsWithIds });
      toast.success(isMulti ? `${valid.length} bilder slått sammen til én kvittering` : "Kvittering skannet!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved skanning av kvittering");
    } finally {
      setScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAppendScan = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter((f) => f.type.startsWith("image/"));
    if (valid.length === 0) { toast.error("Kun bildefiler støttes"); return; }
    if (!receipt?.id) { toast.error("Mangler kvittering"); return; }
    setAppendingScan(true);
    try {
      const formData = new FormData();
      valid.forEach((f) => formData.append("files", f));
      const res = await api.appendScanToReceipt(receipt.id, formData);
      const updated = res.data?.receipt;
      const added = res.data?.added_items ?? 0;
      if (updated) {
        const itemsWithIds = (updated.items || []).map((it, idx) => ({
          ...it,
          _key: it.id || `rcp-${updated.id}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
        }));
        setReceipt({ ...updated, items: itemsWithIds });
      }
      if (added > 0) {
        toast.success(`${added} nye varer lagt til kvitteringen`);
      } else {
        toast.info("Ingen nye varer funnet i bildene");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved å legge til bilder");
    } finally {
      setAppendingScan(false);
      if (appendInputRef.current) appendInputRef.current.value = "";
    }
  };

  const updateReceiptItem = (index, field, value) => {
    if (!receipt) return;
    const items = [...receipt.items];
    items[index] = { ...items[index], [field]: value };
    setReceipt({ ...receipt, items });
  };

  const removeReceiptItem = (index) => {
    if (!receipt) return;
    const items = receipt.items.filter((_, i) => i !== index);
    const total = items.reduce((sum, it) => sum + (parseFloat(it.price) || 0), 0);
    setReceipt({ ...receipt, items, total_amount: total });
  };

  const handleSaveChanges = async () => {
    if (!receipt) return;
    try {
      const cleanItems = (receipt.items || []).map(({ _key, ...rest }) => rest);
      await api.updateReceipt(receipt.id, {
        store_name: receipt.store_name,
        total_amount: receipt.total_amount,
        date: receipt.date,
        items: cleanItems,
        trip_id: receipt.trip_id || "",
      });
      toast.success("Endringer lagret");
    } catch { toast.error("Feil ved lagring"); }
  };

  const handleConfirm = async () => {
    if (!receipt) return;
    setConfirming(true);
    try {
      await handleSaveChanges();
      const res = await api.confirmReceipt(receipt.id);
      toast.success(`${res.data.items_added} varer lagt til i beholdningen!`);
      setReceipt(null);
      loadReceipts();
    } catch { toast.error("Feil ved bekreftelse"); }
    finally { setConfirming(false); }
  };

  const handleRescanReceipt = async (id, e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter((f) => f.type.startsWith("image/"));
    if (valid.length === 0) { toast.error("Kun bildefiler støttes"); return; }
    setRescanningId(id);
    try {
      const fd = new FormData();
      valid.forEach((f) => fd.append("files", f));
      const res = await api.appendScanToReceipt(id, fd);
      const added = res.data?.added_items ?? 0;
      const pantryAdded = res.data?.pantry_added ?? 0;
      if (added > 0) {
        toast.success(`${added} nye varer lagt til kvitteringen${pantryAdded > 0 ? " og beholdningen" : ""}`);
      } else {
        toast.info("Ingen nye varer funnet på bildene");
      }
      loadReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved re-skanning");
    } finally {
      setRescanningId(null);
      if (e.target) e.target.value = "";
    }
  };

  const handleRecomputeExtras = async (id) => {
    try {
      const res = await api.recomputeReceiptExtras(id);
      const n = res.data?.extras_logged || 0;
      if (n === 0) toast.info("Ingen pant, rabatt eller bæreposer funnet på kvitteringen");
      else toast.success(`${n} statistikk-linjer (pant/rabatt/bæreposer) registrert`);
      loadReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved statistikk-oppdatering");
    }
  };


  const handleDeleteReceipt = async (id) => {
    try {
      await api.deleteReceipt(id);
      setReceipts((prev) => prev.filter((r) => r.id !== id));
      toast.success("Kvittering slettet");
    } catch { toast.error("Feil ved sletting"); }
  };

  // Re-open a previously saved receipt for editing
  const handleEditReceipt = (r) => {
    const itemsWithIds = (Array.isArray(r.items) ? r.items : []).map((it, idx) => ({
      ...it,
      _key: it.id || `rcp-${r.id}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
    }));
    setReceipt({ ...r, items: itemsWithIds });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const storageIcon = (type) => {
    const st = STORAGE_TYPES.find((s) => s.value === type);
    if (!st) return null;
    const Icon = st.icon;
    return <Icon className="h-3.5 w-3.5" />;
  };

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto" data-testid="receipt-scan-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Skann kvittering</h1>
          <p className="text-sm text-stone-500 mt-1">
            {selectedTripId
              ? `Tur: ${(trips.find((t) => t.id === selectedTripId)?.name) || activeTrip?.name || "Valgt"}`
              : (activeTrip ? `Aktiv tur: ${activeTrip.name}` : "Ingen tur valgt")}
          </p>
        </div>
      </div>

      {/* Trip selector */}
      {!receipt && trips.length > 0 && (
        <div className="mb-4 bg-white border border-stone-200 rounded-xl p-3 sm:p-4" data-testid="trip-selector-wrapper">
          <label className="block text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Knytt kvitteringen til tur</label>
          <Select value={selectedTripId || "_none"} onValueChange={(v) => setSelectedTripId(v === "_none" ? "" : v)}>
            <SelectTrigger className="w-full" data-testid="trip-selector">
              <SelectValue placeholder="Velg tur (valgfritt)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">Ingen tur</SelectItem>
              {trips.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}{t.is_active ? " (aktiv)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Upload area */}
      {!receipt && (
        <div className="mb-8">
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleScan} className="hidden" data-testid="receipt-file-input" />
          {scanning ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white border-2 border-dashed border-stone-200 rounded-xl">
              <div className="w-10 h-10 border-3 border-navy-800 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-sm font-medium text-stone-600">Analyserer kvittering med AI...</p>
              <p className="text-xs text-stone-400 mt-1">Dette kan ta opptil et minutt for flere bilder</p>
            </div>
          ) : (
            <button onClick={() => fileInputRef.current?.click()} className="w-full flex flex-col items-center justify-center gap-4 py-16 bg-white border-2 border-dashed border-stone-200 rounded-xl hover:border-seawater-500 hover:bg-sailcloth-100/30 transition-all cursor-pointer" data-testid="scan-receipt-btn">
              <div className="w-16 h-16 rounded-2xl bg-sailcloth-100 flex items-center justify-center">
                <Camera className="h-8 w-8 text-navy-800" />
              </div>
              <div className="text-center px-4">
                <p className="text-base font-medium text-stone-700">Ta bilde eller last opp kvittering</p>
                <p className="text-sm text-stone-400 mt-1">AI leser varene og kategoriserer automatisk</p>
                <p className="text-xs text-violet-600 mt-2 font-semibold">📸 Velg flere bilder for å skanne en lang kvittering</p>
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
                <span className="inline-flex items-center gap-1.5 text-xs text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full"><Package className="h-3 w-3" /> Kjølevare</span>
                <span className="inline-flex items-center gap-1.5 text-xs text-cyan-600 bg-cyan-50 px-2.5 py-1 rounded-full"><Snowflake className="h-3 w-3" /> Frysevare</span>
                <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full"><Sun className="h-3 w-3" /> Tørrproviant</span>
              </div>
            </button>
          )}
          {!scanning && (
            <button
              onClick={() => setManualOpen(true)}
              className="w-full mt-3 flex items-center justify-center gap-2 py-3 bg-white border border-stone-200 rounded-xl text-sm font-medium text-stone-700 hover:bg-stone-50 hover:border-stone-300 transition-all"
              data-testid="manual-receipt-btn"
              title="Har du ikkje kvittering å skanne? Legg inn beløpet manuelt."
            >
              <PenLine className="h-4 w-4 text-stone-500" />
              Legg til manuelt (utan skanning)
            </button>
          )}
        </div>
      )}

      {/* Scanned receipt preview */}
      {receipt && (
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden mb-8" data-testid="receipt-preview">
          {receipt.status === "confirmed" && (
            <div className="px-3 sm:px-4 py-2 bg-sailcloth-50 border-b border-sailcloth-200 text-xs text-navy-800" data-testid="confirmed-edit-notice">
              ℹ️ Denne kvitteringen er allerede bekreftet. Endringer her oppdaterer KUN selve kvitterings-loggen — varene i beholdningen påvirkes ikke.
            </div>
          )}
          <div className="p-3 sm:p-4 border-b border-stone-100 bg-stone-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <input
                    value={receipt.store_name}
                    onChange={(e) => setReceipt({ ...receipt, store_name: e.target.value })}
                    placeholder="Butikknavn"
                    className="text-base sm:text-lg font-semibold text-stone-800 bg-white border border-stone-200 rounded px-2 py-1 focus:border-seawater-600 outline-none w-full sm:w-auto"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    data-testid="receipt-store-name"
                  />
                  <input
                    type="date"
                    value={receipt.date}
                    onChange={(e) => setReceipt({ ...receipt, date: e.target.value })}
                    className="text-sm text-stone-500 bg-white border border-stone-200 rounded px-2 py-1 focus:border-seawater-600 outline-none"
                    data-testid="receipt-date"
                  />
                </div>
                {/* Trip selector while editing — change which trip this receipt belongs to */}
                {trips.length > 0 && (
                  <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="receipt-trip-selector-wrapper">
                    <span className="text-xs text-stone-500 font-semibold uppercase tracking-wider">Tur:</span>
                    <Select
                      value={receipt.trip_id || "_none"}
                      onValueChange={(v) => setReceipt({ ...receipt, trip_id: v === "_none" ? "" : v })}
                    >
                      <SelectTrigger className="h-7 text-xs w-48 bg-white" data-testid="receipt-trip-selector">
                        <SelectValue placeholder="Velg tur" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Ingen tur</SelectItem>
                        {trips.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}{t.is_active ? " (aktiv)" : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <p className="text-xs text-stone-500 mt-1">{receipt.items.length} varer funnet{receipt.pages_scanned ? ` · ${receipt.pages_scanned} bilder` : ""}</p>
                {receipt.status !== "confirmed" && (
                  <div className="mt-2" data-testid="append-scan-wrapper">
                    <input
                      ref={appendInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleAppendScan}
                      className="hidden"
                      data-testid="append-scan-input"
                    />
                    <button
                      onClick={() => appendInputRef.current?.click()}
                      disabled={appendingScan}
                      className="inline-flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 text-violet-700 px-2.5 py-1.5 text-xs font-medium hover:bg-violet-100 transition-colors disabled:opacity-60"
                      data-testid="append-scan-btn"
                      title="Legg til flere bilder av samme kvittering"
                    >
                      {appendingScan ? (
                        <>
                          <div className="w-3 h-3 border-2 border-violet-700 border-t-transparent rounded-full animate-spin" />
                          Leser bilder...
                        </>
                      ) : (
                        <>
                          <ImagePlus className="h-3.5 w-3.5" />
                          + Legg til flere bilder
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <span className="text-xs text-stone-400 block">Total</span>
                <div className="flex items-baseline justify-end gap-1">
                  <input
                    type="number"
                    step="0.01"
                    value={receipt.total_amount}
                    onChange={(e) => setReceipt({ ...receipt, total_amount: parseFloat(e.target.value) || 0 })}
                    className="text-xl sm:text-2xl font-bold text-stone-800 bg-white border border-stone-200 rounded px-2 py-1 focus:border-seawater-600 outline-none text-right w-24 sm:w-32"
                    data-testid="receipt-total"
                  />
                  <span className="text-sm text-stone-500">kr</span>
                </div>
              </div>
            </div>
          </div>

          {/* Items list */}
          <div className="divide-y divide-stone-50">
            {receipt.items.map((item, i) => (
              <div key={item._key || item.id || `rcp-item-${i}`} className="px-3 sm:px-4 py-3 group hover:bg-stone-50/50 transition-colors" data-testid={`receipt-item-${i}`}>
                {/* Top row: icon + full-width name + remove */}
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg border ${STORAGE_TYPES.find((s) => s.value === item.storage_type)?.color || "bg-stone-50 text-stone-500 border-stone-200"}`}>
                    {storageIcon(item.storage_type)}
                  </div>
                  <input
                    value={item.name}
                    onChange={(e) => updateReceiptItem(i, "name", e.target.value)}
                    placeholder="Varenavn (rediger om feil)"
                    className="flex-1 min-w-0 text-sm font-medium text-stone-700 bg-transparent border-b border-stone-200 hover:border-stone-300 focus:border-seawater-600 outline-none py-0.5"
                    data-testid={`receipt-item-name-${i}`}
                  />
                  <button onClick={() => removeReceiptItem(i)} className="flex-shrink-0 p-1 rounded hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors" data-testid={`remove-receipt-item-${i}`} title="Fjern vare">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Bottom row: amount, unit, storage, price */}
                <div className="flex items-center gap-2 mt-2 pl-11 flex-wrap">
                  <div className="flex items-center gap-1">
                    <input
                      value={item.amount}
                      onChange={(e) => updateReceiptItem(i, "amount", e.target.value)}
                      placeholder="1"
                      className="w-12 text-xs text-stone-600 bg-white border border-stone-200 rounded px-1.5 py-1 focus:border-seawater-600 outline-none text-center"
                      data-testid={`receipt-item-amount-${i}`}
                    />
                    <input
                      value={item.unit}
                      onChange={(e) => updateReceiptItem(i, "unit", e.target.value)}
                      placeholder="stk"
                      className="w-14 text-xs text-stone-600 bg-white border border-stone-200 rounded px-1.5 py-1 focus:border-seawater-600 outline-none text-center"
                      data-testid={`receipt-item-unit-${i}`}
                    />
                  </div>
                  <Select value={item.storage_type} onValueChange={(v) => updateReceiptItem(i, "storage_type", v)}>
                    <SelectTrigger className="w-32 h-7 text-xs" data-testid={`receipt-item-storage-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STORAGE_TYPES.map((st) => (
                        <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => updateReceiptItem(i, "storage_type", item.storage_type === "non_food" ? "dry" : "non_food")}
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors border ${item.storage_type === "non_food" ? "bg-stone-200 text-stone-700 border-stone-300" : "bg-white text-stone-500 border-stone-200 hover:bg-stone-50"}`}
                    data-testid={`mark-non-food-${i}`}
                    title={item.storage_type === "non_food" ? "Klikk for å gjenopprette som matvare" : "Marker som ikke matvare — vil ikke bli lagt til beholdning"}
                  >
                    {item.storage_type === "non_food" ? (<><X className="h-3 w-3" /> Ikke mat</>) : (<><Wrench className="h-3 w-3" /> Ikke mat</>)}
                  </button>
                  <div className="flex items-center gap-1 ml-auto">
                    <input
                      type="number"
                      step="0.01"
                      value={item.price}
                      onChange={(e) => updateReceiptItem(i, "price", parseFloat(e.target.value) || 0)}
                      className="w-20 text-sm font-semibold text-stone-700 bg-white border border-stone-200 rounded px-2 py-1 focus:border-seawater-600 outline-none text-right"
                      data-testid={`receipt-item-price-${i}`}
                    />
                    <span className="text-xs text-stone-400">kr</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="p-3 sm:p-4 border-t border-stone-100 bg-stone-50 flex items-center justify-between gap-2 flex-wrap">
            <button onClick={() => setReceipt(null)} className="text-sm text-stone-500 hover:text-stone-700 transition-colors" data-testid="cancel-receipt-btn">
              {receipt.status === "confirmed" ? "Lukk" : "Avbryt"}
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={async () => { await handleSaveChanges(); loadReceipts(); }} className="inline-flex items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-xs sm:text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors" data-testid="save-receipt-btn">
                Lagre endringer
              </button>
              {receipt.status !== "confirmed" && (
                <button onClick={handleConfirm} disabled={confirming} className="inline-flex items-center gap-1.5 rounded-md bg-navy-800 px-3 py-2 text-xs sm:text-sm font-medium text-white hover:bg-navy-900 transition-colors disabled:opacity-50" data-testid="confirm-receipt-btn">
                  <Check className="h-4 w-4" />
                  {confirming ? "Bekrefter..." : "Bekreft → beholdning"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Previous receipts */}
      {receipts.length > 0 && !receipt && (
        <div data-testid="receipts-history">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <h2 className="text-xl font-semibold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Tidligere kvitteringer</h2>
            {currentTripRange && (
              <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-md px-3 py-2 text-xs" data-testid="trip-filter-toggle-wrapper">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={currentTripOnly}
                    onChange={(e) => setCurrentTripOnly(e.target.checked)}
                    className="accent-navy-800"
                    data-testid="current-trip-only-toggle"
                  />
                  <span className="font-medium text-stone-700">Vis berre denne turen</span>
                </label>
                <span className="text-stone-400 hidden sm:inline">
                  ({currentTripRange.start.getDate()}.{currentTripRange.start.getMonth() + 1} – {currentTripRange.end.getDate()}.{currentTripRange.end.getMonth() + 1})
                </span>
              </div>
            )}
          </div>
          {filteredReceipts.length === 0 && currentTripOnly ? (
            <div className="bg-white border border-stone-100 rounded-xl p-6 text-center text-sm text-stone-500" data-testid="no-trip-receipts">
              Ingen kvitteringer for denne turen ({currentTripRange?.start.toLocaleDateString("nb-NO")} – {currentTripRange?.end.toLocaleDateString("nb-NO")}).
              <button onClick={() => setCurrentTripOnly(false)} className="ml-1 underline text-navy-800">Vis alle</button>
            </div>
          ) : (
          <div className="space-y-3">
            {filteredReceipts.map((r) => {
              const isExpanded = expandedReceiptIds.has(r.id);
              return (
              <SafeReceiptCard key={r.id} receiptId={r.id} onDelete={handleDeleteReceipt}>
              <div className="bg-white border border-stone-100 rounded-xl overflow-hidden" data-testid={`history-receipt-${r.id}`}>
                <div className="p-3 sm:p-4 flex items-center justify-between gap-2">
                  <button
                    onClick={() => toggleExpanded(r.id)}
                    className="flex-1 min-w-0 text-left hover:bg-stone-50/50 -m-2 p-2 rounded-md transition-colors flex items-start gap-2"
                    data-testid={`toggle-receipt-${r.id}`}
                    title={isExpanded ? "Skjul varer" : "Vis varer"}
                  >
                    <div className="flex-shrink-0 mt-0.5 text-stone-400">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-stone-700 truncate">{asText(r.store_name, "Ukjent butikk")}</span>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0 ${r.status === "confirmed" ? "bg-sailcloth-100 text-navy-800" : "bg-amber-50 text-amber-700"}`}>
                          {r.status === "confirmed" ? "Bekreftet" : "Venter"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-stone-400 mt-1">
                        <span>{asText(r.date)}</span>
                        <span>{(Array.isArray(r.items) ? r.items.length : 0)} varer</span>
                        <span className="text-navy-700 hidden sm:inline">{isExpanded ? "Skjul varer" : "Trykk for å vise varer"}</span>
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm sm:text-base font-semibold text-stone-800">{asNumber(r.total_amount).toLocaleString("nb-NO")} kr</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleRescanReceipt(r.id, e)}
                      className="hidden"
                      id={`header-rescan-input-${r.id}`}
                      data-testid={`header-rescan-input-${r.id}`}
                    />
                    <label
                      htmlFor={`header-rescan-input-${r.id}`}
                      className={`p-1.5 hover:bg-violet-50 rounded-md text-stone-400 hover:text-violet-600 transition-colors cursor-pointer ${rescanningId === r.id ? "opacity-60 pointer-events-none" : ""}`}
                      data-testid={`header-rescan-btn-${r.id}`}
                      title="Skann på nytt — last opp et nytt bilde hvis AI mangla noko"
                    >
                      {rescanningId === r.id ? (
                        <div className="w-3.5 h-3.5 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <ImagePlus className="h-3.5 w-3.5" />
                      )}
                    </label>
                    <button onClick={() => handleEditReceipt(r)} className="p-1.5 hover:bg-stone-100 rounded-md text-stone-500 hover:text-navy-800 transition-colors" data-testid={`edit-receipt-icon-${r.id}`} title="Rediger">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDeleteReceipt(r.id)} className="p-1.5 hover:bg-red-50 rounded-md text-stone-400 hover:text-red-500 transition-colors" data-testid={`delete-receipt-${r.id}`} title="Slett">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-stone-100 bg-stone-50/50" data-testid={`receipt-items-list-${r.id}`}>
                    <div className="px-3 sm:px-4 py-2 border-b border-stone-100 flex flex-wrap items-center gap-2 bg-white/60">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handleRescanReceipt(r.id, e)}
                        className="hidden"
                        id={`rescan-input-${r.id}`}
                        data-testid={`rescan-input-${r.id}`}
                      />
                      <label
                        htmlFor={`rescan-input-${r.id}`}
                        className={`inline-flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 text-violet-700 px-2.5 py-1.5 text-xs font-medium hover:bg-violet-100 cursor-pointer ${rescanningId === r.id ? "opacity-60 pointer-events-none" : ""}`}
                        title="Last opp nye bilder hvis noe mangler"
                        data-testid={`rescan-btn-${r.id}`}
                      >
                        {rescanningId === r.id ? (
                          <><div className="w-3 h-3 border-2 border-violet-700 border-t-transparent rounded-full animate-spin" />Skanner...</>
                        ) : (
                          <><ImagePlus className="h-3.5 w-3.5" /> Skann på nytt</>
                        )}
                      </label>
                      {!(Array.isArray(r.extras) && r.extras.length) && (
                        <button
                          onClick={() => handleRecomputeExtras(r.id)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-orange-200 bg-orange-50 text-orange-700 px-2.5 py-1.5 text-xs font-medium hover:bg-orange-100"
                          data-testid={`recompute-extras-btn-${r.id}`}
                          title="Beregn pant/rabatt/bæreposer fra eksisterende varer"
                        >
                          <Receipt className="h-3.5 w-3.5" /> Oppdater statistikk
                        </button>
                      )}
                    </div>
                    {(Array.isArray(r.items) ? r.items : []).length === 0 ? (
                      <p className="px-4 py-3 text-xs text-stone-400 italic">Ingen varer registrert på denne kvitteringen.</p>
                    ) : (
                      <ul className="divide-y divide-stone-100">
                        {(Array.isArray(r.items) ? r.items : []).map((it, i) => {
                          const st = STORAGE_TYPES.find((s) => s.value === it.storage_type);
                          const Icon = st?.icon;
                          return (
                            <li key={asText(it.id) || `${r.id}-it-${i}`} className="px-3 sm:px-4 py-2 flex items-center gap-3 text-sm" data-testid={`history-item-${r.id}-${i}`}>
                              <div className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-md border ${st?.color || "bg-stone-50 text-stone-500 border-stone-200"}`}>
                                {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-stone-800 font-medium truncate">{asText(it.name, "Ukjent vare")}</p>
                                <p className="text-xs text-stone-400">
                                  {asText(it.amount, "1")} {asText(it.unit, "stk")}{st?.label ? ` · ${st.label}` : ""}
                                </p>
                              </div>
                              <span className="text-sm font-semibold text-stone-700 flex-shrink-0">{asNumber(it.price).toLocaleString("nb-NO")} kr</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {Array.isArray(r.extras) && r.extras.length > 0 && (() => {
                      const byKind = {};
                      for (const e of r.extras) {
                        if (!e || typeof e !== "object") continue;
                        const k = asText(e.kind, "annet") || "annet";
                        const t = byKind[k] || (byKind[k] = { count: 0, price: 0 });
                        t.count += 1;
                        t.price += asNumber(e.price);
                      }
                      const labels = { pant: "Pant", rabatt: "Rabatt", baerepose: "Bæreposer", handlenett: "Handlenett", annet: "Annet" };
                      return (
                        <div className="px-3 sm:px-4 py-2 border-t border-stone-100 bg-orange-50/40 flex flex-wrap gap-3 text-[11px]" data-testid={`receipt-extras-${r.id}`}>
                          {Object.entries(byKind).map(([k, t]) => (
                            <span key={k} className="inline-flex items-baseline gap-1.5 text-orange-800">
                              <span className="font-semibold">{labels[k] || k}:</span>
                              <span>{t.count} stk</span>
                              {t.price !== 0 && (
                                <span className={`${k === "rabatt" ? "text-emerald-700" : "text-orange-800"} font-semibold`}>
                                  {k === "rabatt" && t.price > 0 ? "-" : ""}{Math.abs(t.price).toLocaleString("nb-NO")} kr
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                    <div className="px-3 sm:px-4 py-2 border-t border-stone-100 flex items-center justify-end">
                      <button
                        onClick={() => handleEditReceipt(r)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-navy-700 hover:text-navy-900 transition-colors"
                        data-testid={`open-edit-receipt-${r.id}`}
                      >
                        <Pencil className="h-3 w-3" /> Åpne for redigering
                      </button>
                    </div>
                  </div>
                )}
              </div>
              </SafeReceiptCard>
              );
            })}
          </div>
          )}
        </div>
      )}

      {/* Manual receipt dialog — for kvitteringer utan bilde */}
      <Dialog open={manualOpen} onOpenChange={(o) => { if (!o && !savingManual) setManualOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="manual-receipt-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Legg til kvittering manuelt</DialogTitle>
            <DialogDescription>
              Manglar du fysisk kvittering, eller gløymde å skanne? Skriv inn beløpet her — det blir bokført med ein gong mot månadsbudsjettet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">Sum (kr) *</label>
              <input
                type="text"
                inputMode="decimal"
                value={manualForm.total_amount}
                onChange={(e) => setManualForm({ ...manualForm, total_amount: e.target.value })}
                placeholder="0,00"
                className="w-full rounded-md border border-stone-200 bg-white px-3 py-2.5 text-lg font-semibold focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
                data-testid="manual-total-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">Butikk (valfritt)</label>
              <input
                type="text"
                value={manualForm.store_name}
                onChange={(e) => setManualForm({ ...manualForm, store_name: e.target.value })}
                placeholder="F.eks. Rema 1000, Kiwi, Coop…"
                className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none"
                data-testid="manual-store-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">Dato</label>
              <input
                type="date"
                value={manualForm.date}
                onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
                className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none"
                data-testid="manual-date-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">Notat (valfritt)</label>
              <input
                type="text"
                value={manualForm.notes}
                onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                placeholder="F.eks. Handleliste-uke 32, glømte kvittering…"
                className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none"
                data-testid="manual-notes-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setManualOpen(false)}
                disabled={savingManual}
                className="flex-1 rounded-md border border-stone-200 bg-white px-3 py-2.5 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                data-testid="manual-cancel"
              >
                Avbryt
              </button>
              <button
                onClick={submitManualReceipt}
                disabled={savingManual || !manualForm.total_amount}
                className="flex-1 rounded-md bg-navy-800 px-3 py-2.5 text-sm font-medium text-white hover:bg-navy-900 disabled:opacity-50"
                data-testid="manual-submit"
              >
                {savingManual ? "Lagrar…" : "Legg til"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
